<template>
  <div class="fault-order-container">
    <CardBox title="故障工单" subtitle="Fault work order">
      <!-- 两个统计卡片 -->
      <div class="order-cards">
        <div class="order-card" :style="{ backgroundImage: `url(${faultTaskBg})` }">
          <div class="order-card__top">
            <span class="order-card__label">今日新增</span>
            <span class="order-card__value">{{ countData.todayCount }}</span>
          </div>
          <div class="order-card__bottom">
            <img :src="todayNewIcon" class="order-card__icon" />
            <div class="order-card__left">
              <div class="order-card__subs">
                <div class="order-card__sub">
                  <span class="order-card__sub-label">本月累计</span>
                  <span class="order-card__sub-value">{{ countData.monthCount }}</span>
                </div>
                <div class="order-card__sub">
                  <span class="order-card__sub-label">本月已完成</span>
                  <span class="order-card__sub-value">{{ countData.monthCompleteCount }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="order-card" :style="{ backgroundImage: `url(${faultTaskBg})` }">
          <div class="order-card__top">
            <span class="order-card__label">总累计</span>
              <span class="order-card__value">{{ countData.allCount }}</span>
          </div>
          <div class="order-card__bottom">
            <img :src="totalAccumulatedIcon" class="order-card__icon" />
            <div class="order-card__left">
              <div class="order-card__subs">
                <div class="order-card__sub">
                  <span class="order-card__sub-label">今年已完成</span>
                  <span class="order-card__sub-value">{{ countData.yearCompleteCount }}</span>
                </div>
                <div class="order-card__sub">
                  <span class="order-card__sub-label">待完成</span>
                  <span class="order-card__sub-value order-card__sub-value--danger">{{
                    countData.errorCount
                  }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <!-- 柱状图 -->
      <div class="order-chart">
        <ml-echarts id="faultOrderChart" :option="chartOption" />
      </div>
    </CardBox>
  </div>
</template>

<script>
import { ref, onMounted } from 'vue'
import { useStore } from 'vuex'
import * as echarts from 'echarts'
import { getTabberData, animaction } from '@utils'
import CardBox from './cardBox.vue'
import faultTaskBg from '@assets/image/homeIndex/fault_task_bg.png'
import todayNewIcon from '@assets/image/homeIndex/today_new_icon.png'
import totalAccumulatedIcon from '@assets/image/homeIndex/total_accumulated_icon.png'
import z1Option from '@utils/viewData/z1'

export default {
  name: 'FaultWorkOrderComponent',
  components: { CardBox },
  setup() {
    const { dispatch } = useStore()

    // 故障工单统计数据
    const countData = ref({
      todayCount: 0,
      allCount: 0,
      monthCount: 0,
      monthCompleteCount: 0,
      yearCompleteCount: 0,
      errorCount: 0
    })
    const fetchErrorCountStatistics = async () => {
      const { data } = await getTabberData(dispatch, 'fetchErrorCountStatistics')
      if (data) {
        countData.value = {
          todayCount: data.todayCount || 0,
          allCount: data.allCount || 0,
          monthCount: data.monthCount || 0,
          monthCompleteCount: data.monthCompleteCount || 0,
          yearCompleteCount: data.yearCompleteCount || 0,
          errorCount: data.errorCount || 0
        }
      }
    }

    // 故障工单柱状图数据
    const listData = ref([])
    const listIndex = ref([])
    const listInterval = ref()
    const fetchErrorCountStatisticsList = async () => {
      const { data } = await getTabberData(dispatch, 'fetchErrorCountStatisticsList')
      listData.value = data || []
      listInterval.value = animaction(data, listIndex)
    }

    // 供父组件调用的定时刷新方法（先清除动画定时器再重新拉取）
    const getInit = () => {
      fetchErrorCountStatistics()
      fetchErrorCountStatisticsList()
    }
    const closeTimeInit = () => {
      clearInterval(listInterval.value)
    }
    const refreshData = () => {
      closeTimeInit()
      getInit()
    }

    onMounted(() => {
      getInit()
    })

    return {
      countData,
      listData,
      listIndex,
      faultTaskBg,
      todayNewIcon,
      totalAccumulatedIcon,
      z1Option,
      refreshData
    }
  },
  computed: {
    chartOption() {
      const base = this.z1Option(this.listData, this.listIndex)
      // 图例渐变色
      const completedGradient = new echarts.graphic.LinearGradient(0, 0, 0, 1, [
        { offset: 0, color: '#0080ff' },
        { offset: 1, color: '#33cdff' }
      ])
      const uncompletedGradient = new echarts.graphic.LinearGradient(0, 1, 0, 0, [
        { offset: 0, color: '#ffc000' },
        { offset: 1, color: '#ff8443' }
      ])
      base.legend = {
        show: true,
        top: 0,
        textStyle: { color: '#fff', fontSize: 12 },
        itemWidth: 10,
        itemHeight: 10,
        itemGap: 16,
        data: [
          { name: '已完成', itemStyle: { color: completedGradient } },
          { name: '未完成', itemStyle: { color: uncompletedGradient } }
        ]
      }
      // 轴线颜色
      const axisColor = '#D5EAFF'
      if (base.xAxis && base.xAxis[0]) {
        base.xAxis[0].axisLine.lineStyle.color = axisColor
        base.xAxis[0].axisTick.show = false
        base.xAxis[0].axisLabel.color = axisColor
      }
      if (base.yAxis) {
        base.yAxis.axisLine = { show: true, lineStyle: { color: axisColor } }
        base.yAxis.axisTick = { show: false }
        base.yAxis.axisLabel.color = axisColor
        base.yAxis.splitLine = {
          show: true,
          lineStyle: { color: 'rgba(50, 80, 140, 0.4)', type: 'dashed' }
        }
      }
      // 柱子渐变色
      if (base.series) {
        base.series.forEach(s => {
          if (s.name === '已完成') {
            s.itemStyle = {
              color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                { offset: 0, color: '#0080ff' },
                { offset: 1, color: '#33cdff' }
              ])
            }
          } else if (s.name === '未完成') {
            s.itemStyle = {
              color: new echarts.graphic.LinearGradient(0, 1, 0, 0, [
                { offset: 0, color: '#ffc000' },
                { offset: 1, color: '#ff8443' }
              ])
            }
          }
        })
      }
      // 调整 grid
      base.grid = { top: '35px', left: '2%', right: '4%', bottom: '10px', containLabel: true }
      // 标题样式
      if (base.title) {
        base.title.textStyle.color = axisColor
      }
      return base
    }
  }
}
</script>

<style lang="scss" scoped>
.fault-order-container {
  width: 100%;
  height: calc(45% - 10px);
}

/* 两个统计卡片 */
.order-cards {
  display: flex;
  gap: 10px;
  margin-bottom: 12px;
}
.order-card {
  flex: 1;
  width: 195px;
  height: 106px;
  background-size: 100% 100%;
  background-repeat: no-repeat;
  display: flex;
  flex-direction: column;
  box-sizing: border-box;

  &__top {
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0 12px;
  }
  &__label {
    font-size: 12px;
    color: #d5eaff;
    font-family: 'SourceHanSansCN-Regular', sans-serif;
  }
  &__bottom {
    height: 74px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0 12px;
  }
  &__left {
    display: flex;
    flex-direction: column;
    gap: 6px;
  }
  &__value {
    font-size: 16px;
    color: #fff;
    font-family: 'OPPOSans-B';
    background: linear-gradient(180deg, #ffffff 40%, #00ffff 100%);
    -webkit-background-clip: text;
    background-clip: text;
    -webkit-text-fill-color: transparent;
    filter: drop-shadow(0px 1px 1px rgba(0, 0, 0, 0.25));
  }
  &__subs {
    display: flex;
    flex-direction: column;
    gap: 2px;
  }
  &__sub {
    display: flex;
    align-items: center;
  }
  &__sub-label {
    width: 70px;
    text-align: left;
    font-size: 13px;
    color: rgba(213,234,255,0.85);
  }
  &__sub-value {
    width: 30px;
    text-align: right;
    font-size: 14px;
    color: #fff;
    font-family: 'OPPOSans-B';

    &--danger {
      color: #ff4d4f;
      font-weight: bold;
    }
  }
  &__icon {
    width: 54px;
    height: 50px;
  }
}

/* 柱状图 */
.order-chart {
  width: 100%;
  height: calc(100% - 125px);
}
</style>
